// @ts-nocheck
import { useState } from "react";
import { useListDevices, useSendMessage, useListTemplates } from "../lib/api-client-react";
import { PageHeader } from "../components/layout/PageHeader";
import { useLang } from "../lib/lang";

type Mode = "plain" | "media" | "location";

const API = import.meta.env.VITE_API_URL ?? "";

function mediaUrl(objectPath: string) {
  return `${API}/api/storage${objectPath}`;
}

export function SingleSend() {
  const { t } = useLang();
  const d = t.singleSend;
  const { data: devices } = useListDevices();
  const { data: templates } = useListTemplates();
  const sendMessage = useSendMessage();

  const [mode, setMode] = useState<Mode>("plain");
  const [device, setDevice] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [templateId, setTemplateId] = useState<number | null>(null);
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedTemplate = templates?.find(t => t.id === templateId) ?? null;

  const handleTemplateChange = (id: number | null) => {
    setTemplateId(id);
    if (id !== null) {
      const t = templates?.find(t => t.id === id);
      if (t) setMessage(t.content);
    }
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await sendMessage.mutateAsync({
        data: {
          deviceId: Number(device),
          phoneNumber: phone,
          content: mode === "location" ? `Location: ${lat}, ${lng}` : message,
          templateId: templateId ?? null,
        },
      });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      setPhone(""); setMessage(""); setTemplateId(null);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes("422")) {
        setError("Device is not connected to WhatsApp. Please scan the QR code first.");
      } else {
        setError(msg || "Failed to send message. Please try again.");
      }
    }
  };

  const tabs: { key: Mode; label: string }[] = [
    { key: "plain", label: d.plain },
    { key: "media", label: d.media },
    { key: "location", label: d.location },
  ];

  return (
    <div>
      <PageHeader title={d.title} />

      <div className="px-6 pb-6">
        <div className="max-w-4xl">
          <div className="bg-white rounded-xl shadow-sm border border-[#e9ecef]">
            <div className="px-6 py-4 border-b border-[#e9ecef]">
              <h4 className="text-sm font-semibold text-[#32325d]">{d.sendCustom}</h4>
            </div>

            <div className="flex">
              {/* Tab nav */}
              <div className="w-44 border-r border-[#e9ecef] p-3 shrink-0">
                <ul className="space-y-1">
                  {tabs.map(t => (
                    <li key={t.key}>
                      <button
                        onClick={() => setMode(t.key)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                          mode === t.key
                            ? "gradient-primary text-white font-medium shadow-sm"
                            : "text-[#525f7f] hover:bg-[#f6f9fc]"
                        }`}
                      >
                        {t.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Form */}
              <div className="flex-1 p-6">
                {success && (
                  <div className="mb-4 bg-[#d4edda] border border-[#c3e6cb] text-[#155724] px-4 py-3 rounded-lg text-sm flex items-center gap-2">
                    <i className="fas fa-check-circle" />
                    {d.sent}
                  </div>
                )}

                {error && (
                  <div className="mb-4 bg-[#fff3cd] border border-[#ffc107] text-[#856404] px-4 py-3 rounded-lg text-sm flex items-center gap-2">
                    <i className="fas fa-exclamation-triangle" />
                    {error}
                  </div>
                )}

                <form onSubmit={handleSend} className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-[#525f7f] mb-1.5">{d.device}</label>
                    <select value={device} onChange={e => setDevice(e.target.value)} className="w-full border border-[#cad1d7] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1171ef]/30 focus:border-[#1171ef]" required>
                      <option value="">{d.selectDevice}</option>
                      {devices?.map(d => <option key={d.id} value={d.id}>{d.name} — {d.phoneNumber}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-[#525f7f] mb-1.5">{d.phone}</label>
                    <input value={phone} onChange={e => setPhone(e.target.value)} className="w-full border border-[#cad1d7] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1171ef]/30 focus:border-[#1171ef]" placeholder={d.phonePlaceholder} required />
                  </div>

                  {/* Template selector — visible on plain & media tabs */}
                  {(mode === "plain" || mode === "media") && (
                    <div>
                      <label className="block text-xs font-semibold text-[#525f7f] mb-1.5">
                        {d.template} <span className="font-normal text-[#8898aa]">({t.common.optional})</span>
                      </label>
                      <select
                        value={templateId ?? ""}
                        onChange={e => handleTemplateChange(e.target.value ? Number(e.target.value) : null)}
                        className="w-full border border-[#cad1d7] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1171ef]/30 focus:border-[#1171ef]"
                      >
                        <option value="">{d.selectTemplate}</option>
                        {templates?.map(t => (
                          <option key={t.id} value={t.id}>
                            {t.name}
                            {t.type === "image" ? " 🖼" : t.type === "video" ? " 🎬" : ""}
                            {t.category ? ` (${t.category})` : ""}
                          </option>
                        ))}
                      </select>

                      {/* Template media preview */}
                      {selectedTemplate && selectedTemplate.type === "image" && selectedTemplate.imageUrl && (
                        <div className="mt-2 rounded-lg overflow-hidden border border-[#e9ecef] h-32">
                          <img
                            src={mediaUrl(selectedTemplate.imageUrl)}
                            alt="Template preview"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      {selectedTemplate && selectedTemplate.type === "video" && selectedTemplate.imageUrl && (
                        <div className="mt-2 rounded-lg overflow-hidden border border-[#e9ecef] h-32 bg-black relative">
                          <video
                            src={mediaUrl(selectedTemplate.imageUrl)}
                            className="w-full h-full object-cover"
                            muted
                            playsInline
                          />
                          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <div className="w-9 h-9 rounded-full bg-white/80 flex items-center justify-center">
                              <i className="fas fa-play text-[#1171ef] text-xs ml-0.5" />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {(mode === "plain" || mode === "media") && (
                    <div>
                      <label className="block text-xs font-semibold text-[#525f7f] mb-1.5">{d.message}</label>
                      <textarea
                        value={message}
                        onChange={e => setMessage(e.target.value)}
                        rows={5}
                        className="w-full border border-[#cad1d7] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1171ef]/30 focus:border-[#1171ef] resize-none"
                        placeholder={d.messagePlaceholder}
                        required
                      />
                    </div>
                  )}

                  {mode === "media" && !templateId && (
                    <div>
                      <label className="block text-xs font-semibold text-[#525f7f] mb-1.5">{d.attachMedia}</label>
                      <div className="border-2 border-dashed border-[#cad1d7] rounded-lg p-6 text-center hover:border-[#1171ef] transition-colors cursor-pointer">
                        <i className="fas fa-cloud-upload-alt text-2xl text-[#8898aa] mb-2 block" />
                        <p className="text-sm text-[#8898aa]">{d.uploadHint}</p>
                        <p className="text-xs text-[#cad1d7] mt-1">{d.uploadTypes}</p>
                      </div>
                    </div>
                  )}

                  {mode === "location" && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-[#525f7f] mb-1.5">{d.latitude}</label>
                        <input value={lat} onChange={e => setLat(e.target.value)} className="w-full border border-[#cad1d7] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1171ef]/30 focus:border-[#1171ef]" placeholder="40.7128" required />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-[#525f7f] mb-1.5">{d.longitude}</label>
                        <input value={lng} onChange={e => setLng(e.target.value)} className="w-full border border-[#cad1d7] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1171ef]/30 focus:border-[#1171ef]" placeholder="-74.0060" required />
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end pt-2">
                    <button type="submit" disabled={sendMessage.isPending} className="gradient-primary text-white px-6 py-2 rounded-lg text-sm font-medium shadow-sm hover:opacity-90 disabled:opacity-50 flex items-center gap-2">
                      <i className="fas fa-paper-plane" />
                      {sendMessage.isPending ? d.sending : d.sendMessage}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
